if (!customElements.get('teylens-shape-carousel')) {
  class TeylensShapeCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.items = Array.from(this.querySelectorAll('[data-shape-item]'));
      this.stage = this.querySelector('[data-shape-stage]');
      this.controls = this.querySelector('[data-shape-controls]');
      this.current = this.querySelector('[data-shape-current]');
      this.status = this.querySelector('[data-shape-status]');
      if (!this.items.length || !this.stage || !this.controls || !this.current) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.media = window.matchMedia('(max-width: 749px)');
      this.index = 0;
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.onMediaChange = () => this.render(false);
      this.media.addEventListener('change', this.onMediaChange);
      this.addEventListener('click', (event) => {
        const previous = event.target.closest('[data-shape-prev]');
        const next = event.target.closest('[data-shape-next]');
        const pick = event.target.closest('[data-shape-select]');
        if (pick && performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          return;
        }
        if (previous) this.select(this.index - 1);
        else if (next) this.select(this.index + 1);
        else if (pick) this.select(this.items.indexOf(pick.closest('[data-shape-item]')));
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (!this.contains(document.activeElement)) return;
        if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.select(event.key === 'Home' ? 0 : this.items.length - 1);
        }
      }, { signal });
      this.stage.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.pointerType === 'mouse') return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { passive: true, signal });
      this.stage.addEventListener('pointerup', (event) => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const dx = event.clientX - this.pointer.x;
        const dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.suppressClickUntil = performance.now() + 350;
        this.select(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true, signal });
      this.stage.addEventListener('pointercancel', () => { this.pointer = null; }, { signal });
      this.onBlockSelect = (event) => {
        if (event.detail?.sectionId && String(event.detail.sectionId) !== this.dataset.sectionId) return;
        const index = this.items.findIndex((item) => item.dataset.blockId === String(event.detail?.blockId));
        if (index >= 0) this.select(index, false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
      this.controls.hidden = false;
      this.current.hidden = false;
      this.stage.tabIndex = 0;
      this.setAttribute('aria-roledescription', this.dataset.carouselLabel || 'carousel');
      this.classList.add('is-enhanced');
      this.render(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.media?.removeEventListener('change', this.onMediaChange);
      this.controller = null;
      this.classList.remove('is-enhanced');
      this.items?.forEach((item) => {
        item.hidden = false;
        item.inert = false;
        const pick = item.querySelector('[data-shape-select]');
        if (pick) { pick.hidden = true; pick.tabIndex = -1; }
      });
      if (this.controls) this.controls.hidden = true;
      if (this.current) { this.current.hidden = true; this.current.replaceChildren(); }
      if (this.stage) this.stage.removeAttribute('tabindex');
      this.removeAttribute('aria-roledescription');
      this.pointer = null;
    }

    select(index, announce = true) {
      this.index = ((index % this.items.length) + this.items.length) % this.items.length;
      this.render(announce);
    }

    render(announce) {
      const count = this.items.length;
      const radius = this.media.matches ? 1 : 2;
      const focused = document.activeElement;
      const focusedItem = focused?.closest('[data-shape-item]');
      const focusedCurrent = this.current.contains(focused);
      const focusedCurrentLink = focusedCurrent && focused.closest('a');
      let restoreSelectionFocus = false;
      this.items.forEach((item, index) => {
        let offset = (index - this.index + count) % count;
        if (offset > count / 2) offset -= count;
        const visible = Math.abs(offset) <= radius;
        const selected = index === this.index;
        if (focusedItem === item && !visible) restoreSelectionFocus = true;
        item.hidden = !visible;
        item.inert = !visible;
        item.style.setProperty('--ty-shape-x', `${offset * (this.media.matches ? 37 : 20.5)}%`);
        item.style.setProperty('--ty-shape-scale', selected ? '1' : this.media.matches ? '.53' : Math.abs(offset) === 1 ? '.76' : '.54');
        item.style.setProperty('--ty-shape-opacity', selected ? '1' : Math.abs(offset) === 1 ? '.87' : '.65');
        item.style.setProperty('--ty-shape-layer', String(3 - Math.abs(offset)));
        const pick = item.querySelector('[data-shape-select]');
        if (pick) {
          pick.hidden = !visible;
          pick.tabIndex = visible ? 0 : -1;
          pick.setAttribute('aria-pressed', String(selected));
          if (selected) pick.setAttribute('aria-current', 'true');
          else pick.removeAttribute('aria-current');
        }
      });
      const active = this.items[this.index];
      const caption = active.querySelector('[data-shape-caption]');
      this.current.replaceChildren(caption.cloneNode(true));
      if (restoreSelectionFocus) active.querySelector('[data-shape-select]')?.focus({ preventScroll: true });
      if (focusedCurrentLink) {
        const nextFocus = this.current.querySelector('a') || active.querySelector('[data-shape-select]');
        nextFocus?.focus({ preventScroll: true });
      }
      this.querySelectorAll('[data-shape-prev], [data-shape-next]').forEach((button) => {
        button.disabled = count < 2;
      });
      if (announce && this.status) {
        const title = active.dataset.shapeTitle || '';
        this.status.textContent = (this.dataset.statusTemplate || '[[title]], [[index]] of [[total]]')
          .replaceAll('[[title]]', title)
          .replaceAll('[[index]]', String(this.index + 1))
          .replaceAll('[[total]]', String(count));
      }
    }
  }
  customElements.define('teylens-shape-carousel', TeylensShapeCarousel);
}
